import { getheaders } from '../../service/authService';

/**
 * Service for handling Guesty message-related API calls
 */

// Get all Guesty webhook messages
export const getGuestyWebhookMessages = async (cosCustomerId = null) => {
    let url = "/goapi/guesty/webhook-messages";
    
    if (cosCustomerId) {
        url += `?cosCustomerId=${cosCustomerId}`;
    }
    
    const requestOptions = {
        method: 'GET',
        headers: getheaders(),
        redirect: 'follow'
    };

    return fetch(url, requestOptions);
};

// Get a specific Guesty webhook message by ID
export const getGuestyWebhookMessageById = async (id) => {
    const requestOptions = {
        method: 'GET',
        headers: getheaders(),
        redirect: 'follow'
    };

    return fetch(`/api/guesty/webhook-messages/${id}`, requestOptions);
};

// Get raw Guesty webhook message by ID
export const getGuestyRawWebhookMessageById = async (id) => {
    const requestOptions = {
        method: 'GET',
        headers: getheaders(),
        redirect: 'follow'
    };

    return fetch(`/api/guesty/webhook-messages/raw/${id}`, requestOptions);
};

// Get count of failed Guesty messages
export const getGuestyFailedMessagesCount = async () => {
    const requestOptions = {
        method: 'GET',
        headers: getheaders(),
        redirect: 'follow'
    };

    return fetch("/api/guesty/webhook-messages/failed/count", requestOptions);
};

// Toggle Guesty message status
export const toggleGuestyMessageStatus = async (id, forceStatus = null) => {
    const requestOptions = {
        method: 'POST',
        headers: getheaders(),
        redirect: 'follow'
    };

    // If forceStatus is provided, add it to the request body
    if (forceStatus) {
        requestOptions.body = JSON.stringify({ targetStatus: forceStatus });
        requestOptions.headers.append("Content-Type", "application/json");
    }

    return fetch(`/api/guesty/webhook-messages/${id}/toggle-status`, requestOptions);
};

// Get all users (customers)
export const getAllUsers = async () => {
    const requestOptions = {
        method: 'GET', 
        headers: getheaders(), 
        redirect: 'follow'
    };

    return fetch("/api/user/all", requestOptions);
};

/**
 * Utility functions for message processing
 */

// Convert snake_case message to camelCase
export const convertMessageToCamelCase = (message) => ({
    id: message.id,
    receivedTime: message.received_time,
    processingStatus: message.processing_status,
    listingAddress: message.listing_address,
    checkInTime: message.check_in_time,
    checkOutTime: message.check_out_time,
    cosCustomerId: message.cos_customer_id,
    messageType: message.message_type,
    cosJobId: message.cos_job_id,
    propertyId: message.property_id,
    rawMessage: message.raw_message,
    parsingError: message.parsing_error,
    reservationId: message.reservation_id
});

// Filter messages from the last 2 months
export const filterRecentMessages = (messages) => {
    const twoMonthsAgo = new Date();
    twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
    
    return messages.filter(message => {
        const receivedTime = new Date(message.receivedTime);
        return receivedTime >= twoMonthsAgo;
    });
};

// Sort messages by received time (latest first)
export const sortMessagesByTime = (messages) => {
    return messages.sort((a, b) => {
        return new Date(b.receivedTime) - new Date(a.receivedTime);
    });
};

// Check for missing IDs in a sequence
export const checkForMissingIds = (messages) => {
    if (!messages || messages.length === 0) return [];
    
    // Extract all IDs and convert to numbers
    const ids = messages.map(message => parseInt(message.id, 10))
                        .filter(id => !isNaN(id)); // Filter out any non-numeric IDs
    
    if (ids.length === 0) return [];
    
    // Sort IDs in ascending order
    ids.sort((a, b) => a - b);
    
    // Find missing IDs in the sequence
    const missing = [];
    for (let i = 1; i < ids.length; i++) {
        // Check if there's a gap between consecutive IDs
        const gap = ids[i] - ids[i-1];
        if (gap > 1) {
            // Add all missing IDs in the gap
            for (let j = ids[i-1] + 1; j < ids[i]; j++) {
                missing.push(j);
            }
        }
    }
    
    return missing;
};

// Create customer map from users
export const createCustomerMap = (users) => {
    const map = {};
    users.forEach(user => {
        if (user.cos_customer_id && user.cos_customer_name && 
            (user.role === 'ADMIN' || user.role === 'AGENT')) {
            map[user.cos_customer_id] = user.cos_customer_name;
        }
    });
    return map;
};

// Create customers list from users
export const createCustomersList = (users) => {
    const customersList = [];
    users.forEach(user => {
        if (user.cos_customer_id && user.cos_customer_name && 
            (user.role === 'ADMIN' || user.role === 'AGENT')) {
            customersList.push({
                id: user.cos_customer_id,
                name: user.cos_customer_name,
                messageCount: 0 // Initialize message count
            });
        }
    });
    return customersList;
};

// Update customers with message counts
export const updateCustomersWithMessageCounts = (customers, messages) => {
    const messageCounts = {};
    messages.forEach(message => {
        if (message.cosCustomerId) {
            messageCounts[message.cosCustomerId] = (messageCounts[message.cosCustomerId] || 0) + 1;
        }
    });
    
    // Update customers with message counts
    const updatedCustomers = customers.map(customer => ({
        ...customer,
        messageCount: messageCounts[customer.id] || 0
    }));
    
    // Sort customers: those with messages first, then those without
    updatedCustomers.sort((a, b) => b.messageCount - a.messageCount);
    
    return updatedCustomers;
};

// Extract unique addresses from messages
export const extractUniqueAddresses = (messages) => {
    const addresses = messages
        .filter(message => message.listingAddress)
        .map(message => message.listingAddress);
    
    // Remove duplicates and sort alphabetically
    return [...new Set(addresses)].sort();
};

// Extract unique reservation IDs from messages
export const extractUniqueReservationIds = (messages) => {
    const reservationIds = messages
        .filter(message => message.reservationId)
        .map(message => message.reservationId);
    
    // Remove duplicates and sort alphabetically
    return [...new Set(reservationIds)].sort();
};

// Extract status types and counts
export const extractStatusTypesAndCounts = (messages) => {
    const statuses = {};
    messages.forEach(message => {
        if (message.processingStatus) {
            statuses[message.processingStatus] = (statuses[message.processingStatus] || 0) + 1;
        }
    });
    
    // Convert to array and sort by priority
    const statusOrder = ['FAILED', 'ERROR', 'PENDING', 'PROCESSED', 'IGNORED'];
    const statusList = Object.keys(statuses).sort((a, b) => {
        return statusOrder.indexOf(a) - statusOrder.indexOf(b);
    });
    
    return { statusTypes: statusList, statusCounts: statuses };
};

// Apply text search filter to messages
export const applyTextSearchFilter = (messages, searchText, customerMap) => {
    if (!searchText || searchText.length === 0) {
        return messages;
    }
    
    const searchLower = searchText.toLowerCase();
    return messages.filter(message => {
        // Search in customer name if available
        const customerName = customerMap[message.cosCustomerId] || '';
        
        return (
            (message.cosCustomerId && message.cosCustomerId.toLowerCase().includes(searchLower)) ||
            (customerName && customerName.toLowerCase().includes(searchLower)) ||
            (message.listingAddress && message.listingAddress.toLowerCase().includes(searchLower)) ||
            (message.parsingError && message.parsingError.toLowerCase().includes(searchLower)) ||
            (message.reservationId && message.reservationId.toLowerCase().includes(searchLower))
        );
    });
};

// Parse API response helper
export const parseApiResponse = async (response) => {
    if (response.status === 200) {
        const result = await response.json();
        return { success: true, data: result };
    } else {
        try {
            const text = await response.text();
            try {
                // Try to parse the error response as JSON
                const errorData = JSON.parse(text);
                return { 
                    success: false, 
                    error: errorData.message || "API request failed" 
                };
            } catch (e) {
                // If parsing fails, use the raw text
                return { 
                    success: false, 
                    error: text || "API request failed" 
                };
            }
        } catch (error) {
            return { 
                success: false, 
                error: "An error occurred while processing the response" 
            };
        }
    }
};

// Generate color highlighting for same-day check-in/check-out pairs
export const generateDateHighlighting = (messages) => {
    const dateColorMap = new Map();
    const colors = [
        '#FFE6E6', '#E6F3FF', '#E6FFE6', '#FFF0E6', '#F0E6FF',
        '#FFE6F0', '#E6FFFF', '#F5FFE6', '#FFE6CC', '#E6E6FF'
    ];
    let colorIndex = 0;
    
    // Group messages by property and date
    const propertyDateGroups = new Map();
    
    messages.forEach(message => {
        if (!message.listingAddress) return;
        
        const checkInDate = message.checkInTime ? new Date(message.checkInTime).toDateString() : null;
        const checkOutDate = message.checkOutTime ? new Date(message.checkOutTime).toDateString() : null;
        
        const property = message.listingAddress;
        
        if (!propertyDateGroups.has(property)) {
            propertyDateGroups.set(property, new Map());
        }
        
        const propertyDates = propertyDateGroups.get(property);
        
        // Track check-in dates
        if (checkInDate) {
            if (!propertyDates.has(checkInDate)) {
                propertyDates.set(checkInDate, { checkIns: [], checkOuts: [] });
            }
            propertyDates.get(checkInDate).checkIns.push({
                messageId: message.id,
                type: 'checkIn'
            });
        }
        
        // Track check-out dates
        if (checkOutDate) {
            if (!propertyDates.has(checkOutDate)) {
                propertyDates.set(checkOutDate, { checkIns: [], checkOuts: [] });
            }
            propertyDates.get(checkOutDate).checkOuts.push({
                messageId: message.id,
                type: 'checkOut'
            });
        }
    });
    
    // Find same-day check-in/check-out pairs and assign colors
    propertyDateGroups.forEach((propertyDates, property) => {
        propertyDates.forEach((dateInfo, date) => {
            // If we have both check-ins and check-outs on the same date for the same property
            if (dateInfo.checkIns.length > 0 && dateInfo.checkOuts.length > 0) {
                const color = colors[colorIndex % colors.length];
                colorIndex++;
                
                // Assign the same color to all check-ins and check-outs on this date
                [...dateInfo.checkIns, ...dateInfo.checkOuts].forEach(item => {
                    const key = `${item.messageId}-${item.type}`;
                    dateColorMap.set(key, color);
                });
            }
        });
    });
    
    return dateColorMap;
};

// Get highlight color for a specific message and date type
export const getDateHighlightColor = (messageId, dateType, colorMap) => {
    const key = `${messageId}-${dateType}`;
    return colorMap.get(key) || null;
};
